import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import localeEN from '@angular/common/locales/en';
import localeUK from '@angular/common/locales/uk';
import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DefaultMatCalendarRangeStrategy, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimations, provideNoopAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';

import { environment } from '@lyra/environments/environment';
import { authInterceptor, provideAuthService } from '@mark/common-lib/services';

import { routes } from './app.routes';
import { provideCoreConfig } from './core/core.config';
import { provideExternalReferrer } from './core/providers/external-referrer.provider';
import { provideMaterialIconsConfig } from './shared/material-icon.config';

registerLocaleData(localeEN);
registerLocaleData(localeUK);

export const MY_FORMATS = {
  ...MAT_MOMENT_DATE_FORMATS,
  display: {
    ...MAT_MOMENT_DATE_FORMATS.display,
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
  },
};

const ICONS: Record<string, string> = {
  mark_wb_sunny: 'assets/svg/mark_wb_sunny.svg',
  mark_check: 'assets/svg/mark_check.svg',
  mark_column: 'assets/svg/mark_column.svg',
  mark_warning: 'assets/svg/mark_warning.svg',
  mark_info: 'assets/svg/mark_info.svg',
  mark_info_circle: 'assets/svg/mark_info_circle.svg',
  calendar: 'assets/svg/calendar.svg',
  mark_document: 'assets/svg/mark_document.svg',
  mark_success: 'assets/svg/mark_success.svg',
  mark_error: 'assets/svg/mark_error.svg',
  mark_send_email: 'assets/svg/mark_send_email.svg',
  mark_send_phone: 'assets/svg/mark_send_phone.svg',
  mark_link: 'assets/svg/mark_link.svg',
  mark_link_2: 'assets/svg/mark_link_2.svg',
  mark_copy: 'assets/svg/mark_copy.svg',
  mark_share: 'assets/svg/mark_share.svg',
  mark_arrow: 'assets/svg/mark_arrow.svg',
  mark_arrow2: 'assets/svg/mark_arrow2.svg',
  mark_mood_bad: 'assets/svg/mark_mood_bad.svg',
  mark_mood_terrible: 'assets/svg/mark_mood_terrible.svg',
  mark_mood_normal: 'assets/svg/mark_mood_normal.svg',
  mark_mood_good: 'assets/svg/mark_mood_good.svg',
  mark_mood_awesome: 'assets/svg/mark_mood_awesome.svg',
  mark_mood_empty: 'assets/svg/mark_mood_empty.svg',
  read_recommendations: 'assets/svg/read_recommendations.svg',
  taking_supplements: 'assets/svg/taking_supplements.svg',
  wellness_tracker: 'assets/svg/wellness_tracker.svg',
  mark_warning2: 'assets/svg/mark_warning2.svg',
  love_uk: 'assets/svg/love_uk.svg',
  mark_logo: 'assets/svg/mark_logo.svg',
  'app-store': 'assets/svg/app-store.svg',
  'google-play': 'assets/svg/google-play.svg',
  social_facebook: 'assets/svg/social_facebook.svg',
  social_instagram: 'assets/svg/social_instagram.svg',
  social_linkedin: 'assets/svg/social_linkedin.svg',
  error_server: 'assets/svg/error_server.svg',
  error_404: 'assets/svg/error_404.svg',
  mark_info_blue: 'assets/svg/mark_info_blue.svg',
  amai: 'assets/svg/amai.svg',
  bh_download: 'assets/svg/bh_download.svg',
  mark_food: 'assets/svg/mark_food.svg',
  mark_liquid: 'assets/svg/mark_liquid.svg',
  mark_setting: 'assets/svg/mark_setting.svg',
  mark_info_dark_blue: 'assets/svg/mark_info_dark_blue.svg',
  mark_chevron_left: 'assets/svg/mark_chevron_left.svg',
  mark_question: 'assets/svg/mark_question.svg',
  mark_female: 'assets/svg/mark_female.svg',
  mark_male: 'assets/svg/mark_male.svg',
  answer_none_of_above: 'assets/svg/answer_none_of_above.svg',
  yes: 'assets/svg/yes.svg',
  no: 'assets/svg/no.svg',
  allergy: 'assets/svg/allergy.svg',
  chronic_disease: 'assets/svg/chronic_disease.svg',
  oncology: 'assets/svg/oncology.svg',
  pill_intolerance: 'assets/svg/pill_intolerance.svg',
  serious_disease: 'assets/svg/serious_disease.svg',
  surgical_intervention: 'assets/svg/surgical_intervention.svg',
  mark_chevron_right: 'assets/svg/mark_chevron_right.svg',
  fm_health_regular: 'assets/svg/fm_health_regular.svg',
  fm_health_pregnant: 'assets/svg/fm_health_pregnant.svg',
  fm_health_menopause: 'assets/svg/fm_health_menopause.svg',
  fm_health_irregular: 'assets/svg/fm_health_irregular.svg',
  pregnancy_and_supplementation: 'assets/svg/pregnancy_and_supplementation.svg',
  mark_long_arrow: 'assets/svg/mark_long_arrow.svg',
  save_progress: 'assets/svg/save_progress.svg',
  payment_successful: 'assets/svg/payment_successful.svg',
  google: 'assets/svg/google.svg',
  microsoft: 'assets/svg/microsoft.svg',
  apple: 'assets/svg/apple.svg',
  facebook: 'assets/svg/facebook.svg',
  linkedIn: 'assets/svg/linkedIn.svg',
  pill: 'assets/svg/pill.svg',
  '4936eb44-61fa-4d1c-8405-ba6007be036e': 'assets/svg/4936eb44-61fa-4d1c-8405-ba6007be036e.svg',
  '7439d368-bedc-4c28-99be-ec05732a65b9': 'assets/svg/7439d368-bedc-4c28-99be-ec05732a65b9.svg',
  '6e7e9333-d0a2-4690-b176-e6352c079f4a': 'assets/svg/6e7e9333-d0a2-4690-b176-e6352c079f4a.svg',
  '67021a86-54de-402b-873a-049421d2b0b3': 'assets/svg/67021a86-54de-402b-873a-049421d2b0b3.svg',
  schedule_interval: 'assets/svg/schedule_interval.svg',
  schedule_several_times_day: 'assets/svg/schedule_several_times_day.svg',
  schedule_hours: 'assets/svg/schedule_hours.svg',
  schedule_days: 'assets/svg/schedule_days.svg',
  clock: 'assets/svg/clock.svg',
  calendar_2: 'assets/svg/calendar_2.svg',

  goals_delivery: 'assets/svg/landing/goals_delivery.svg',
  goals_star: 'assets/svg/landing/goals_star.svg',
  goals_trust: 'assets/svg/landing/goals_trust.svg',
  scale_dot: 'assets/svg/landing/scale_dot.svg',
  chevron_right: 'assets/svg/landing/chevron_right.svg',
  chevron_left: 'assets/svg/landing/chevron_left.svg',
  energy: 'assets/svg/landing/energy.svg',
  immunity: 'assets/svg/landing/immunity.svg',
  mood: 'assets/svg/landing/mood.svg',
  brain: 'assets/svg/landing/brain.svg',
  sleep: 'assets/svg/landing/sleep.svg',
  dosages: 'assets/svg/landing/dosages.svg',
  schedule: 'assets/svg/landing/schedule.svg',
  tracking: 'assets/svg/landing/tracking.svg',
  star: 'assets/svg/landing/star.svg',
  pause: 'assets/svg/landing/pause.svg',
  shipping: 'assets/svg/landing/shipping.svg',
  fda: 'assets/svg/landing/fda.svg',
  edit: 'assets/svg/landing/edit.svg',

  star_line: 'assets/svg/landing/star_line.svg',
  chevron_top: 'assets/svg/landing/chevron_top.svg',

  offer_v3_stars: 'assets/svg/landing/offer_v3/stars.svg',
  offer_v3_members: 'assets/svg/landing/offer_v3/members.svg',
  offer_v3_tested: 'assets/svg/landing/offer_v3/tested.svg',
  offer_v3_arrow_down: 'assets/svg/landing/offer_v3/arrow_down.svg',
  offer_v3_clock: 'assets/svg/landing/offer_v3/clock.svg',
  health_profile_energy: 'assets/svg/landing/offer_v3/health_profile_energy.svg',
  health_profile_health: 'assets/svg/landing/offer_v3/health_profile_health.svg',
  health_profile_sleep: 'assets/svg/landing/offer_v3/health_profile_sleep.svg',
  health_profile_stress: 'assets/svg/landing/offer_v3/health_profile_stress.svg',
  goal_energy: 'assets/svg/landing/offer_v3/goal_energy.svg',
  goal_focus: 'assets/svg/landing/offer_v3/goal_focus.svg',
  goal_sexual_wellness: 'assets/svg/landing/offer_v3/goal_sexual_wellness.svg',
  goal_weight: 'assets/svg/landing/offer_v3/goal_weight.svg',
  goal_sleep: 'assets/svg/landing/offer_v3/goal_sleep.svg',
  goal_strength: 'assets/svg/landing/offer_v3/goal_strength.svg',
  goal_skin_hair: 'assets/svg/landing/offer_v3/goal_skin_hair.svg',
  goal_immune: 'assets/svg/landing/offer_v3/goal_immune.svg',
  goal_stress: 'assets/svg/landing/offer_v3/goal_stress.svg',
  offer_v3_long_arrow: 'assets/svg/landing/offer_v3/long_arrow.svg',
  offer_v3_star_quotes: 'assets/svg/landing/offer_v3/star_quotes.svg',
  offer_v3_offer_benifit: 'assets/svg/landing/offer_v3/offer_benifit.svg',
  offer_v3_cancel: 'assets/svg/landing/offer_v3/cancel.svg',
  offer_v3_doc: 'assets/svg/landing/offer_v3/doc.svg',
  offer_v3_pause: 'assets/svg/landing/offer_v3/pause.svg',
  offer_v3_shipping: 'assets/svg/landing/offer_v3/shipping.svg',
  offer_v3_ships: 'assets/svg/landing/offer_v3/ships.svg',
  offer_v3_packaging: 'assets/svg/landing/offer_v3/packaging.svg',
  offer_v3_success: 'assets/svg/landing/offer_v3/success.svg',
  offer_v3_cancel_circle: 'assets/svg/landing/offer_v3/cancel_circle.svg',
  offer_v3_cancel_check: 'assets/svg/landing/offer_v3/check.svg',
  offer_v3_cancel_uncheck: 'assets/svg/landing/offer_v3/uncheck.svg',

  offer_tick_circle: 'assets/svg/landing/offer/tick_circle.svg',
  offer_close_square: 'assets/svg/landing/offer/close_square.svg',
  offer_money: 'assets/svg/landing/offer/money.svg',
  offer_delivery: 'assets/svg/landing/offer/delivery.svg',
  offer_star: 'assets/svg/landing/offer/star.svg',
  offer_moneys: 'assets/svg/landing/offer/moneys.svg',
  offer_promise_star: 'assets/svg/landing/offer/promise_star.svg',
  offer_promise_tick: 'assets/svg/landing/offer/promise_tick.svg',
  offer_promise_calendar: 'assets/svg/landing/offer/promise_calendar.svg',
  offer_tag_energy: 'assets/svg/landing/offer/tag_energy.svg',
  offer_tag_focus: 'assets/svg/landing/offer/tag_focus.svg',
  offer_tag_sexual_wellness: 'assets/svg/landing/offer/tag_sexual_wellness.svg',
  offer_tag_weight: 'assets/svg/landing/offer/tag_weight.svg',
  offer_tag_sleep: 'assets/svg/landing/offer/tag_sleep.svg',
  offer_tag_strength: 'assets/svg/landing/offer/tag_strength.svg',
  offer_tag_skin_hair: 'assets/svg/landing/offer/tag_skin_hair.svg',
  offer_tag_immune: 'assets/svg/landing/offer/tag_immune.svg',
  offer_tag_stress: 'assets/svg/landing/offer/tag_stress.svg',
  offer_edit: 'assets/svg/landing/offer/edit.svg',
  offer_exit_user: 'assets/svg/landing/offer/exit_user.svg',
  offer_exit_task: 'assets/svg/landing/offer/exit_task.svg',

  short_quiz_email_illustration: 'assets/svg/landing/short-quiz/email-illustration.svg',
  short_quiz_confirm_illustration: 'assets/svg/landing/short-quiz/confirm-illustration.svg',
  short_quiz_back_chevron: 'assets/svg/landing/short-quiz/back-chevron.svg',
  short_quiz_info_circle: 'assets/svg/landing/short-quiz/info.svg',

  hold_expired_illustration: 'assets/svg/landing/short-quiz/expired-illustration.svg',
  quiz_error_illustration: 'assets/svg/landing/short-quiz/error-illustration.svg',

  ali_clinical: 'assets/svg/landing/ali-pages/clinical.svg',
  ali_pill: 'assets/svg/landing/ali-pages/pill.svg',
  ali_shield: 'assets/svg/landing/ali-pages/shield.svg',
  ali_shield_tick: 'assets/svg/landing/ali-pages/shield_tick.svg',
  ali_delivery: 'assets/svg/landing/ali-pages/delivery.svg',
  ali_money: 'assets/svg/landing/ali-pages/money.svg',
  ali_document: 'assets/svg/landing/ali-pages/document.svg',
  ali_close_square: 'assets/svg/landing/ali-pages/close_square.svg',
  ali_user_tag: 'assets/svg/landing/ali-pages/user_tag.svg',
  ali_verify: 'assets/svg/landing/ali-pages/verify.svg',
  ali_tick_circle: 'assets/svg/landing/ali-pages/tick_circle.svg',
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideCoreConfig(),
    provideMaterialIconsConfig(ICONS),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ anchorScrolling: 'enabled' }),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        // Nested feature routes (e.g. vmain/short-quiz) inherit the ancestor :lang param, so deep
        // components can still read params['lang'] for navigation (default 'emptyOnly' drops it).
        paramsInheritanceStrategy: 'always',
      }),
    ),
    provideExternalReferrer(),
    provideClientHydration(withEventReplay()),
    provideHttpClient(withFetch(), withInterceptorsFromDi(), withInterceptors([authInterceptor])),
    { provide: MAT_DATE_LOCALE, useValue: 'en' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    DefaultMatCalendarRangeStrategy,
    MatRangeDateSelectionModel,
    provideAuthService(environment),
    ...(isPlatformBrowser(typeof globalThis !== 'undefined' ? 'browser' : 'server')
      ? [provideAnimations()]
      : [provideNoopAnimations()]),
  ],
};
